<template>
  <v-layout column>
    <LKPublicationsCardRecommendationRpshV3IndexMedianas
      v-if="canManageRpshV3IndexMedianas"
      :publication-id="data.id"
      :recommendation-algorithm="data.recommendation_algorithm"
      @uploaded="onAggregatedGenerationCreated"
    />

    <LKPublicationsCardRecommendationRpshV3MassGeneration
      v-if="canManageRpshV3IndexMedianas"
      :publication-id="data.id"
    />

    <div
      v-if="showIndividualGenerationControls"
      class="LKPublicationsCardRecommendation__profile-generation"
    >
      <div
        class="LKPublicationsCardRecommendation__actions"
      >
        <BTextField
          v-if="profile.is_superuser && !isRpshV3"
          v-model="recomendation.schoolId"
          label="Код школы"
          class="LKPublicationsCardRecommendation__school-input"
        />
        <v-btn
          v-if="showIndividualGenerationControls"
          color="primary"
          rounded
          :loading="recomendation.isLoading"
          :disabled="isRecomendationButtonDisabled"
          outlined
          class="LKPublicationsCardRecommendation__button"
          @click="onRecomendationClick"
          v-text="recomendationText"
        />
      </div>
      <span v-if="recomendation.updatedAt && recomendation.status != 'done'">
        Последний запрос профиля производился
        {{ recomendation.updatedAt | dateFormatter }}
      </span>
      <span v-html="recomendationMonitor" />
    </div>

    <div
      v-if="canGenerateAggregated"
      class="LKPublicationsCardRecommendation__aggregated"
    >
      <LKPublicationsCardRecommendationAggregatedCsv
        :publication-id="data.id"
        @open-modal="onAggregatedGenerationClick"
        @generated="onAggregatedGenerationCreated"
      />

      <LKPublicationsCardRecommendationAggregatedTable
        ref="aggregatedTable"
        :publication-id="data.id"
      />
    </div>

    <LKPublicationsCardRecommendationAggregatedModal
      ref="aggregatedModal"
      :publication-id="data.id"
      @generated="onAggregatedGenerationCreated"
    />
  </v-layout>
</template>

<script>
import BTextField from '@/components/forms/BTextField.vue'
import { LkRecomendationApi } from '@/api/resources'
import { downloadLinkByGet } from '@/helpers/downloadBlob'
import { rootMapper } from '@/store/modules/root'
import LKPublicationsCardRecommendationAggregatedModal from './LKPublicationsCardRecommendationAggregatedModal.vue'
import LKPublicationsCardRecommendationAggregatedCsv from './LKPublicationsCardRecommendationAggregatedCsv.vue'
import LKPublicationsCardRecommendationAggregatedTable from './LKPublicationsCardRecommendationAggregatedTable.vue'
import LKPublicationsCardRecommendationRpshV3IndexMedianas from './LKPublicationsCardRecommendationRpshV3IndexMedianas.vue'
import LKPublicationsCardRecommendationRpshV3MassGeneration from './LKPublicationsCardRecommendationRpshV3MassGeneration.vue'

const INITIAL_STATUS_REFRESH_DELAY = 1000
const MAX_STATUS_REFRESH_DELAY = 16000

export default {
  name: 'LKPublicationsCardRecommendation',
  components: {
    BTextField,
    LKPublicationsCardRecommendationAggregatedModal,
    LKPublicationsCardRecommendationAggregatedCsv,
    LKPublicationsCardRecommendationAggregatedTable,
    LKPublicationsCardRecommendationRpshV3IndexMedianas,
    LKPublicationsCardRecommendationRpshV3MassGeneration,
  },
  props: {
    active: Boolean,
    data: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    recomendation: {
      status: null,
      params: null,
      updatedAt: null,
      isLoading: false,
      schoolId: null,
    },
    statusRefreshTimerId: null,
    statusRefreshDelay: INITIAL_STATUS_REFRESH_DELAY,
  }),
  computed: {
    ...rootMapper.mapGetters(['profile']),
    isRecomendationButtonDisabled() {
      return !this.profile.is_superuser && this.recomendation.status === 'in_progress'
    },
    canGenerateAggregated() {
      return this.profile.is_superuser
    },
    isRpshV3() {
      const algorithm = this.data.recommendation_algorithm || ''
      return algorithm.startsWith('rpsh_v3')
    },
    showIndividualGenerationControls() {
      return !this.profile.is_superuser || !this.isRpshV3
    },
    canManageRpshV3IndexMedianas() {
      return this.canGenerateAggregated && this.isRpshV3
    },
    recomendationMonitor() {
      const r = this.recomendation
      const p = r.params
      const algorithm = this.data.recommendation_algorithm || ''
      if (!p || Object.keys(p).length <= 0 || this.recomendation.status === 'done') {
        return ''
      }
      const text = [
        'Не завершено заполнение минимального количества анкет для получения профиля <br>',
        '<strong>Данные мониторинга</strong>',
        `Анкеты обучающихся: <strong>${p.actual.stu} из ${p.totals.stu}</strong>`,
        `Анкеты учителей: <strong>${p.actual.tea} из ${p.totals.tea}</strong>`,
        `Анкеты родителей: <strong>${p.actual.par} из ${p.totals.par}</strong>`,
      ]
      if (algorithm !== 'rpsh2024' && !algorithm.startsWith('rpsh_v3')) {
        text.push(
          `Анкеты классных руководителей: <strong>${p.actual.clr} из ${p.totals.clr}</strong>`,
          `Чек-лист организации: <strong>${p.actual.check_list} из ${p.totals.check_list}</strong>`,
        )
      }
      text.push(`Анкета администрации: <strong>${p.actual.adm} из ${p.totals.adm}</strong>`)
      return text.join('<br />')
    },
    recomendationText() {
      if (this.profile.is_superuser) {
        return 'Перегенерировать профиль для указанного кода'
      }
      if (!this.recomendation.status) {
        return ''
      }
      switch (this.recomendation.status) {
        case 'not_requested_yet':
        case 'not_ready':
          return 'Получить профиль'
        case 'in_progress':
          return 'Профиль формируется'
        case 'done':
          return 'Скачать профиль'
        default:
          return ''
      }
    },
  },
  watch: {
    active: {
      async handler(v) {
        if (v && !this.profile.is_superuser) {
          this.statusRefreshDelay = INITIAL_STATUS_REFRESH_DELAY
          await this.getRecomendationStatus()
          this.syncStatusRefresh()
          return
        }

        this.stopStatusRefresh()
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.stopStatusRefresh()
  },
  methods: {
    startStatusRefresh() {
      if (
        this.statusRefreshTimerId
        || !this.active
        || this.profile.is_superuser
        || this.recomendation.status === 'done'
      ) return

      const delay = this.statusRefreshDelay
      this.statusRefreshTimerId = setTimeout(async () => {
        this.statusRefreshTimerId = null
        await this.getRecomendationStatus()
        this.syncStatusRefresh()
      }, delay)
      this.statusRefreshDelay = Math.min(delay * 2, MAX_STATUS_REFRESH_DELAY)
    },
    stopStatusRefresh() {
      if (this.statusRefreshTimerId) clearTimeout(this.statusRefreshTimerId)
      this.statusRefreshTimerId = null
      this.statusRefreshDelay = INITIAL_STATUS_REFRESH_DELAY
    },
    syncStatusRefresh() {
      if (this.active && !this.profile.is_superuser && this.recomendation.status !== 'done') {
        this.startStatusRefresh()
        return
      }

      this.stopStatusRefresh()
    },
    async getRecomendationStatus() {
      if (this.recomendation.isLoading) return

      try {
        const api = new LkRecomendationApi()
        this.recomendation.isLoading = true
        const {
          status,
          params,
          updated_at,
        } = await api.getRecomendationStatus(this.data.id)
        this.recomendation.status = status
        this.recomendation.params = params
        this.recomendation.updatedAt = updated_at
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.recomendation.isLoading = false
      }
    },
    onAggregatedGenerationClick() {
      this.$refs.aggregatedModal.open()
    },
    onAggregatedGenerationCreated() {
      this.$refs.aggregatedTable?.getData()
    },
    async onRecomendationClick() {
      const isAdmin = this.profile.is_superuser
      if (isAdmin && this.isRpshV3) return
      if (isAdmin && !this.recomendation.schoolId) {
        this.$notice.warning('Заполните код школы')
        return
      }
      const api = new LkRecomendationApi()
      this.recomendation.isLoading = true
      try {
        if (isAdmin) {
          await api.generateOne(this.data.id, {
            school_id: this.recomendation.schoolId,
          })
          this.$notice.info(`Запущена перегенерация профиля по школе ${this.recomendation.schoolId}`)
          this.recomendation.status = 'in_progress'
        } else if (
          !this.recomendation.status
          || ['not_ready', 'not_requested_yet'].includes(this.recomendation.status)
        ) {
          await api.generate(this.data.id)
          this.recomendation.status = 'in_progress'
        } else if (this.recomendation.status === 'done') {
          downloadLinkByGet(api.getDownloadUrl(this.data.id))
        }
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.recomendation.isLoading = false
        this.syncStatusRefresh()
      }
    },
  },
}
</script>
<style lang="scss">
.LKPublicationsCardRecommendation {
  &__profile-generation {
    margin-top: 18px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: flex-end;
    margin-bottom: 14px;
  }

  &__aggregated {
    margin-top: 14px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.10);
    border-radius: 4px;
  }

  &__aggregated-header {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__school-input {
    width: 220px;
    margin-bottom: 0 !important;
  }

  &__button {
    min-height: 36px;
    margin: 0 !important;
  }
}
</style>
